
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function AboutPage() {
  const team = [
    {
      name: 'Alex Chen',
      role: 'CEO & Founder',
      bio: 'Former AI researcher at Google with 10+ years in machine learning and business automation.',
      image: 'https://public.readdy.ai/ai/img_res/fc005b67f8e1e4edaa968f5ac79e06db.jpg'
    },
    {
      name: 'Sarah Martinez',
      role: 'CTO',
      bio: 'Ex-Microsoft engineer specializing in enterprise AI solutions and scalable automation systems.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20CTO%20headshot%2C%20confident%20female%20tech%20executive%2C%20modern%20office%20background%2C%20technology%20leader%20portrait%2C%20sophisticated%20professional%20lighting%2C%20clean%20corporate%20style&width=300&height=300&seq=sarah-cto&orientation=squarish'
    },
    {
      name: 'David Kim',
      role: 'Head of Strategy',
      bio: 'Former McKinsey consultant with expertise in digital transformation and business process optimization.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20strategy%20executive%20headshot%2C%20confident%20business%20consultant%2C%20modern%20office%20background%2C%20strategic%20leader%20portrait%2C%20sophisticated%20professional%20lighting%2C%20clean%20corporate%20style&width=300&height=300&seq=david&orientation=squarish'
    }
  ];

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation First',
      description: 'We push the boundaries of what\'s possible with A.I. to deliver cutting-edge solutions.'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Client Success',
      description: 'Your success is our success. We\'re committed to delivering measurable results.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Trust & Security',
      description: 'We prioritize data security and maintain the highest standards of confidentiality.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Continuous Growth',
      description: 'We constantly evolve our solutions to stay ahead of industry trends and needs.'
    }
  ];

  const milestones = [
    { year: '2020', event: 'Founded Talynt AI with a vision to democratize business automation' },
    { year: '2021', event: 'Launched our first A.I. agent platform, serving 50+ businesses' },
    { year: '2022', event: 'Expanded to serve 200+ clients across multiple industries' },
    { year: '2023', event: 'Achieved $2.5M in client revenue generation through our A.I. solutions' },
    { year: '2024', event: 'Serving 500+ businesses with advanced A.I. automation systems' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                  Building the Future of <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Business Automation</span>
                </h1>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  At Talynt AI, we believe every business deserves access to intelligent automation that drives growth, reduces costs, and enhances customer experiences.
                </p>
                <Button size="lg" href="/contact">
                  Partner With Us
                </Button>
              </div>
              
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20AI%20technology%20office%20environment%2C%20professional%20team%20collaboration%20workspace%2C%20futuristic%20business%20automation%20center%2C%20clean%20tech%20company%20headquarters%2C%20innovative%20workspace%20with%20holographic%20displays&width=600&height=500&seq=about-hero&orientation=landscape"
                  alt="Talynt AI Office"
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                To empower businesses of all sizes with intelligent A.I. agents that automate growth, streamline operations, and create exceptional customer experiences. We're not just building technology—we're building the future of how businesses operate.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${value.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Meet Our Team</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our leadership team combines decades of experience in AI, technology, and business strategy.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {team.map((member, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg text-center">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-32 h-32 rounded-full object-cover mx-auto mb-6"
                  />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                  <div className="text-blue-600 font-medium mb-4">{member.role}</div>
                  <p className="text-gray-600">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Journey</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From startup to industry leader, here's how we've grown to serve hundreds of businesses.
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-600 to-purple-600"></div>
              
              <div className="space-y-12">
                {milestones.map((milestone, index) => (
                  <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                      <div className="bg-white rounded-xl p-6 shadow-lg">
                        <div className="text-2xl font-bold text-blue-600 mb-2">{milestone.year}</div>
                        <p className="text-gray-700">{milestone.event}</p>
                      </div>
                    </div>
                    
                    <div className="relative z-10">
                      <div className="w-4 h-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></div>
                    </div>
                    
                    <div className="w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-24 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-6">Impact by the Numbers</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
              <div>
                <div className="text-4xl md:text-5xl font-bold mb-2">500+</div>
                <div className="text-blue-100">Businesses Served</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold mb-2">$2.5M</div>
                <div className="text-blue-100">Revenue Generated</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold mb-2">10,000+</div>
                <div className="text-blue-100">Hours Automated</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold mb-2">98%</div>
                <div className="text-blue-100">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gray-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Join Our Success Story?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Let's discuss how our A.I. solutions can transform your business operations and drive sustainable growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" href="/contact">
                Schedule a Consultation
              </Button>
              <Button variant="outline" size="lg" href="/case-studies" className="border-gray-400 text-gray-300 hover:border-white hover:text-white">
                View Our Work
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
